// Trail paths along the drawn head (territory-visuals.md §3, lane C). The reference draws a
// trail as the head's own path, so its turns are arcs at the turn radius. Ours used to rebuild
// the trail every frame from the trail's cell centres, straightened and smoothed, which turned
// each turn into two or three straight pieces joined at sharp 30–45° kinks.
//
// `TrailTracks` keeps, per head, the positions it was drawn at (one point per TRACK_STEP_CELLS
// of travel; the own head predicted, the others interpolated at the render tick). When a trail
// starts, its path begins at the head's last drawn position on its own land; from then on the
// trail is that recorded path to the current head (a frame hitch is a straight step). The cell
// centres are only the fallback for the part of a trail the client
// never saw drawn: a head first seen mid-trail (joined, came into view, RESET), or a long break
// in the drawn path (a hidden tab, a long stall). That part is drawn the old way
// (`trailPolyline`) and joined to the recorded path.
//
// Pure (no canvas); the renderer calls `update` once per frame with the heads it draws.

import type { ArenaDrawHead } from './renderer.js';
import type { ArenaTrail } from './store.js';

/** A new point is recorded once the drawn head moved this far (cells). */
export const TRACK_STEP_CELLS = 0.2;
/**
 * A frame hitch is drawn as a straight step to the head (a turn during a 0.4 s hitch is at most
 * ≈ 54°, so the chord stays within ≈ 0.25 cell of the path). A break longer than this — more new
 * trail cells in one frame, or a jump this far (a hidden tab, a long stall) — ends the recorded
 * path: what came before is drawn from its cells. Without a trail, a jump farther than
 * TRACK_GAP_CELLS (a respawn) restarts the path.
 */
export const TRACK_BRIDGE_MAX_CELLS = 6;
export const TRACK_GAP_CELLS = 2;
/** Points kept for a head without a trail (where it last was on its land). */
const IDLE_POINTS = 32;
/** Points per trail before the path is thinned (every other point dropped). */
const MAX_POINTS = 4000;
/** Frames a head may go undrawn before its track is dropped (when it has no trail). */
const STALE_FRAMES = 120;

interface Track {
  /** Drawn positions, flat [x, y, …], oldest first. */
  pts: number[];
  /** Point index where the current trail's recorded path starts; -1 = no trail. */
  start: number;
  /** The trail's epoch and length at the last update (a lower length or new epoch restarts). */
  epoch: number;
  len: number;
  /** Trail cells before the recorded path (0 = the path covers the whole trail). */
  prefix: number;
  /** The head's heading where the recorded path starts (trims the fallback part). */
  startHeading: number;
  /** The frame this head was last drawn in. */
  frame: number;
}

export interface TrailSource {
  readonly cols: number;
  readonly trails: ReadonlyMap<number, ArenaTrail>;
  ownerAtPoint(x: number, y: number): number;
}

export class TrailTracks {
  private readonly tracks = new Map<number, Track>();
  private frame = 0;

  /** Records this frame's drawn heads and follows each trail's start and end. */
  update(heads: readonly ArenaDrawHead[], src: TrailSource): void {
    this.frame++;
    for (const hd of heads) {
      let t = this.tracks.get(hd.playerId);
      if (!t) {
        t = { pts: [], start: -1, epoch: -1, len: 0, prefix: 0, startHeading: 0, frame: 0 };
        this.tracks.set(hd.playerId, t);
      }
      const trail = src.trails.get(hd.playerId);
      const len = trail?.cells.length ?? 0;
      const n = t.pts.length;
      const lx = n > 0 ? (t.pts[n - 2] as number) : Number.NaN;
      const ly = n > 0 ? (t.pts[n - 1] as number) : Number.NaN;
      const d = n > 0 ? Math.hypot(hd.x - lx, hd.y - ly) : Number.POSITIVE_INFINITY;
      const trailing = t.start >= 0 && len > 0 && trail !== undefined && trail.epoch === t.epoch;
      const fresh = trailing ? len - t.len : 0;
      if (
        n === 0 ||
        (!trailing && d > TRACK_GAP_CELLS) ||
        (trailing && (d > TRACK_BRIDGE_MAX_CELLS || fresh > TRACK_BRIDGE_MAX_CELLS))
      ) {
        // A break in the drawn path: what came before is only known by its cells.
        t.pts = [hd.x, hd.y];
        if (t.start >= 0) {
          t.start = 0;
          t.prefix = len;
          t.startHeading = hd.heading;
        }
      } else if (d >= TRACK_STEP_CELLS) {
        t.pts.push(hd.x, hd.y);
      }
      t.frame = this.frame;
      if (len === 0 || !trail) {
        t.start = -1;
        t.len = 0;
        t.prefix = 0;
        if (t.pts.length > IDLE_POINTS * 2) t.pts.splice(0, t.pts.length - IDLE_POINTS * 2);
        continue;
      }
      if (t.start < 0 || trail.epoch !== t.epoch || len < t.len) this.begin(t, hd, src, len);
      t.epoch = trail.epoch;
      t.len = len;
      if (t.pts.length > MAX_POINTS * 2) thin(t);
    }
    for (const [id, t] of this.tracks) {
      if (this.frame - t.frame > STALE_FRAMES && (src.trails.get(id)?.cells.length ?? 0) === 0) {
        this.tracks.delete(id);
      }
    }
  }

  /**
   * The trail polyline (cells) for player `id`: the recorded path from where it left its land to
   * the head, after the fallback cell-centre part it never saw drawn; `fallback` builds that
   * part (and the whole trail when there is no track).
   */
  polyline(
    id: number,
    cells: readonly number[],
    head: ArenaDrawHead | null,
    fallback: (cells: readonly number[], head: ArenaDrawHead | null) => number[],
  ): number[] {
    const t = this.tracks.get(id);
    if (!t || t.start < 0 || t.frame !== this.frame || cells.length !== t.len) {
      return fallback(cells, head);
    }
    const raw = t.pts.slice(t.start * 2);
    if (head) {
      const n = raw.length;
      if (n < 2 || raw[n - 2] !== head.x || raw[n - 1] !== head.y) raw.push(head.x, head.y);
    }
    // One corner-cutting pass: at a low frame rate the drawn positions are up to ~1.5 cells
    // apart and a turn shows as a few 30–40° steps; this halves them (at 60 fps it is a no-op
    // to the eye). The ends stay put (the land edge, the head).
    const path = cutCorners(raw);
    if (t.prefix <= 0) return path;
    const px = path[0] as number;
    const py = path[1] as number;
    const pre = fallback(cells.slice(0, Math.min(t.prefix, cells.length)), {
      playerId: id,
      x: px,
      y: py,
      heading: t.startHeading,
    });
    // `pre` ends at the path's first point; join without repeating it.
    return pre.concat(path.slice(2));
  }

  /** Forget every track (a new room). */
  clear(): void {
    this.tracks.clear();
  }

  /** Tracks held (tests). */
  get size(): number {
    return this.tracks.size;
  }

  /** A trail just started (or restarted): its path begins at the last drawn point on own land. */
  private begin(t: Track, hd: ArenaDrawHead, src: TrailSource, len: number): void {
    const n = t.pts.length / 2;
    let j = -1;
    for (let i = n - 1; i >= 0; i--) {
      if (src.ownerAtPoint(t.pts[i * 2] as number, t.pts[i * 2 + 1] as number) === hd.playerId) {
        j = i;
        break;
      }
    }
    if (j >= 0) {
      // Recorded from the land's edge on (the own head, predicted, is already out; others,
      // interpolated, are still on their land when the first trail cell arrives).
      t.start = j;
      t.prefix = 0;
    } else {
      // Seen first off its land: the cells so far are the fallback.
      t.start = n - 1;
      t.prefix = len;
    }
    t.startHeading = hd.heading;
  }
}

/** One Chaikin pass (¼–¾) over an open polyline, ends kept. */
function cutCorners(p: readonly number[]): number[] {
  if (p.length < 6) return p.slice();
  const out: number[] = [p[0] as number, p[1] as number];
  for (let i = 0; i + 3 < p.length; i += 2) {
    const ax = p[i] as number;
    const ay = p[i + 1] as number;
    const bx = p[i + 2] as number;
    const by = p[i + 3] as number;
    out.push(
      ax * 0.75 + bx * 0.25,
      ay * 0.75 + by * 0.25,
      ax * 0.25 + bx * 0.75,
      ay * 0.25 + by * 0.75,
    );
  }
  out.push(p[p.length - 2] as number, p[p.length - 1] as number);
  return out;
}

/** Halves a long path's points (keeps the start point and the newest). */
function thin(t: Track): void {
  const n = t.pts.length / 2;
  const out: number[] = [];
  let start = -1;
  for (let i = 0; i < n; i++) {
    const keep = i === t.start || i === n - 1 || (i > t.start && (i - t.start) % 2 === 0);
    if (i < t.start || keep) {
      if (i === t.start) start = out.length / 2;
      out.push(t.pts[i * 2] as number, t.pts[i * 2 + 1] as number);
    }
  }
  t.pts = out;
  t.start = start;
}
